import type { Schema } from './api';

/** НР/СП offered by the 812/пр and 774/пр tables for one norm application; the endpoint applies nothing. */
export type NrSpSuggestion = Schema['NrSpSuggestion'];
export type NrSpOrigin = Schema['PositionNrSp']['origin'];
export type NrSpRate = Schema['NrSpRate'];
export type Territory = NrSpSuggestion['territory'];
type Policy = Schema['PositionPolicy-Output'];

export const territories: Record<Territory, string> = {
  territory: 'Территория — вне районов Крайнего Севера и приравненных к ним местностей',
  mprks: 'МПРКС — местности, приравненные к районам Крайнего Севера',
  rks: 'РКС — районы Крайнего Севера',
};
const statusText: Record<NrSpRate['status'], string> = {
  found: 'найден', absent: 'норматив не найден', ambiguous: 'неоднозначно', invalid: 'код не распознан',
};
// A basis line as the server writes it: «НР 100% от ФОТ: 812/пр (редакция № 2), приложение, …».
const basisLine = /^(?:НР|СП) \d+(?:\.\d+)?% от ФОТ: (?:812|774)\/пр /;

/** A percent is shown only for an exact found row; otherwise the status in words. */
export function rateText(label: 'НР' | 'СП', rate: NrSpRate) {
  return rate.status === 'found' && rate.percent != null ? `${label} ${rate.percent} %` : `${label}: ${statusText[rate.status]}`;
}

/** Printed variants of an ambiguous row in one column; the estimator chooses, not the code. */
export function variantText(rate: NrSpRate, column: string) {
  return rate.candidates.map((v) => `${v.rates[column] ?? '—'} % — ${v.condition ?? 'без условия'}`).join('; ');
}

/** Why a side has no percent, once per distinct reason. */
export function reasons(s: NrSpSuggestion) {
  return [...new Set([s.overhead, s.profit].filter((r) => r.status !== 'found').map((r) => r.reason ?? 'Причина не указана'))];
}

/** Orders whose edition is not confirmed by a primary source. */
export function unverifiedOrders(s: NrSpSuggestion) {
  return [...new Set([s.overhead.source, s.profit.source]
    .filter((source): source is Schema['NrSpSource'] => !!source && !source.status_verified).map((source) => source.order))];
}

/** Substitution needs both percents from exact rows; a half-found pair is not offered. */
export function canSubstitute(s: NrSpSuggestion) {
  return s.overhead.status === 'found' && s.profit.status === 'found' && s.overhead.percent != null && s.profit.percent != null;
}

/** The basis stored with substituted rates: both table rows and every caveat, as the server wrote them. */
export function substitutionSource(s: NrSpSuggestion) {
  return [s.overhead.basis, s.profit.basis, ...s.caveats].filter(Boolean).join('\n');
}

/** Copy only the immutable evidence identity into a saved position policy. */
export function selectionFromSuggestion(s: NrSpSuggestion): Schema['NrSpSelection'] {
  const selectionRate = (rate: NrSpRate): Schema['NrSpSelectionRate'] => ({
    code: rate.code,
    row: rate.row,
    percent: rate.percent!,
    source_order: rate.source?.order ?? null,
    source_revision: rate.source?.revision ?? null,
    source_sha256: rate.source?.sha256 ?? null,
    source_status_verified: rate.source?.status_verified ?? null,
  });
  return {
    application_id: s.application_id,
    norm_id: s.norm_id,
    territory: s.territory,
    resource_version_id: s.resource_version_id,
    resource_sha256: s.resource_sha256,
    overhead: selectionRate(s.overhead),
    profit: selectionRate(s.profit),
  };
}

/** «Подставить в ставку» changes only НР, СП and their basis, and only in the form.
 *  Indices, coefficients and unrelated basis lines stay; an earlier substitution is replaced, not repeated. */
export function substitute(policy: Policy, s: NrSpSuggestion): Policy {
  if (!canSubstitute(s)) return policy;
  const kept = policy.source.split('\n').filter((line) => line.trim() && !basisLine.test(line.trim()) && !s.caveats.includes(line.trim()));
  return { ...policy, overhead_percent: s.overhead.percent, profit_percent: s.profit.percent, nrsp_selection: selectionFromSuggestion(s), source: [substitutionSource(s), ...kept].join('\n') };
}

/** The position's own rates equal the suggestion; common rates never count as a match. */
export function matchesSuggestion(policy: Policy | undefined, s: NrSpSuggestion) {
  const same = (value: string | null | undefined, percent: string | null) =>
    value != null && value !== '' && percent != null && Number(value) === Number(percent);
  const selection = policy?.nrsp_selection;
  const sourceIdentity = (rate: NrSpRate) => ({
    source_order: rate.source?.order ?? null,
    source_revision: rate.source?.revision ?? null,
    source_sha256: rate.source?.sha256 ?? null,
    source_status_verified: rate.source?.status_verified ?? null,
  });
  const selectedRateMatches = (selected: Schema['NrSpSelectionRate'], current: NrSpRate) =>
    selected.code === current.code && selected.row === current.row && selected.percent === current.percent &&
    selected.source_order === sourceIdentity(current).source_order && selected.source_revision === sourceIdentity(current).source_revision &&
    selected.source_sha256 === sourceIdentity(current).source_sha256 && selected.source_status_verified === sourceIdentity(current).source_status_verified;
  const evidenceMatches = !selection || (
    selection.application_id === s.application_id && selection.norm_id === s.norm_id && selection.territory === s.territory &&
    selection.resource_version_id === s.resource_version_id && selection.resource_sha256 === s.resource_sha256 &&
    selectedRateMatches(selection.overhead, s.overhead) && selectedRateMatches(selection.profit, s.profit)
  );
  return !!policy && canSubstitute(s) && evidenceMatches && same(policy.overhead_percent, s.overhead.percent) && same(policy.profit_percent, s.profit.percent);
}

/** A position's own policy starts from the common coefficients, indices and basis, never from the
 *  common НР and СП: an empty percent keeps the norm's rate, so a coefficient edit does not turn
 *  the norm's НР/СП into a manual rate. */
export function positionStart(common: Policy | undefined): Policy {
  return { ...(common ?? { coefficients: {}, indices: {}, source: '' }), overhead_percent: null, profit_percent: null, nrsp_selection: null };
}

/** The percent an empty НР or СП field of a position keeps: the norm's, when that line came from the norm. */
export function normRate(line: Schema['NrSpLine'] | undefined) {
  return line?.basis && line.percent != null ? line.percent : null;
}

const originWords: Record<NrSpOrigin, string> = {
  norm: 'по норме (автоматически)',
  selection: 'по норме, закреплены за позицией',
  manual: 'заданы для позиции вручную',
  default: 'общие ставки сметы',
  absent: 'не определены',
};

/** Where the server took a position's НР and СП from, in words; the server decides, not the form. */
export function originText(origin: NrSpOrigin | null | undefined) {
  return origin ? originWords[origin] : 'источник ставок не указан';
}
